import type { CampaignCreationCapabilities } from "@adpilot/shared";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { currencyLabel } from "./money";
import type { SponsoredDisplayDraft, UpdateDraftField } from "./sponsored-display-draft";

export function CampaignFields({
  draft,
  portfolios,
  currency,
  dailyBudgetLimits,
  submitAttempted,
  onUpdate,
}: {
  draft: SponsoredDisplayDraft;
  portfolios: Array<{ id: string; name: string }>;
  currency: string;
  dailyBudgetLimits: CampaignCreationCapabilities["dailyBudget"];
  submitAttempted: boolean;
  onUpdate: UpdateDraftField;
}) {
  const { t } = useTranslation("campaignCreation");
  const portfolioItems = [
    { value: null, label: t("sd.campaign.portfolioPlaceholder") },
    ...portfolios.map((portfolio) => ({ value: portfolio.id, label: portfolio.name })),
  ];
  const budget = Number(draft.dailyBudget);
  const budgetInvalid =
    submitAttempted &&
    (!Number.isFinite(budget) || budget < dailyBudgetLimits.min || budget > dailyBudgetLimits.max);
  return (
    <FieldGroup className="max-w-[350px] gap-4" data-slot="sd-campaign-fields">
      <Field>
        <FieldLabel htmlFor="sd-campaign-name">{t("sd.campaign.name")} *</FieldLabel>
        <Input
          id="sd-campaign-name"
          value={draft.campaignName}
          onChange={(event) => onUpdate("campaignName", event.target.value)}
          maxLength={128}
        />
      </Field>
      <Field>
        <FieldLabel>{t("sd.campaign.portfolio")}</FieldLabel>
        <Select
          items={portfolioItems}
          value={draft.portfolioId}
          onValueChange={(value) => onUpdate("portfolioId", value)}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={null}>{t("sd.campaign.portfolioPlaceholder")}</SelectItem>
              {portfolios.map((portfolio) => (
                <SelectItem key={portfolio.id} value={portfolio.id}>
                  {portfolio.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </Field>
      <Field data-invalid={submitAttempted && !draft.startDate}>
        <FieldLabel htmlFor="sd-start-date">{t("sd.campaign.startDate")} *</FieldLabel>
        <Input
          id="sd-start-date"
          aria-invalid={submitAttempted && !draft.startDate}
          min={format(new Date(), "yyyy-MM-dd")}
          type="date"
          value={draft.startDate}
          onChange={(event) => onUpdate("startDate", event.target.value)}
        />
        {submitAttempted && !draft.startDate ? (
          <FieldError match={true}>{t("sd.validation.startDateRequired")}</FieldError>
        ) : null}
      </Field>
      <Field>
        <FieldLabel htmlFor="sd-end-date">{t("sd.campaign.endDate")}</FieldLabel>
        <Input
          id="sd-end-date"
          min={draft.startDate}
          type="date"
          value={draft.endDate}
          onChange={(event) => onUpdate("endDate", event.target.value)}
        />
      </Field>
      <Field data-invalid={budgetInvalid}>
        <FieldLabel htmlFor="sd-daily-budget">{t("sd.campaign.dailyBudget")} *</FieldLabel>
        <InputGroup>
          <InputGroupAddon>{currencyLabel(currency)}</InputGroupAddon>
          <InputGroupInput
            id="sd-daily-budget"
            aria-invalid={budgetInvalid}
            inputMode="decimal"
            min="0"
            step="0.01"
            type="number"
            value={draft.dailyBudget}
            onChange={(event) => onUpdate("dailyBudget", event.target.value)}
          />
        </InputGroup>
        {budgetInvalid ? (
          <FieldError match={true}>
            {t("sd.validation.budgetRange", {
              currency: currency === "USD" ? "$" : `${currency} `,
              min: dailyBudgetLimits.min,
              max: dailyBudgetLimits.max,
            })}
          </FieldError>
        ) : null}
      </Field>
    </FieldGroup>
  );
}
